import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { route } from 'nextjs-routes';
import type { ParsedUrlQuery } from 'querystring';
import type { UrlObject } from 'url';

import { addTrailingSlash } from '@/utils/urlUtils';

import { captureMessage } from '@/lib/errors';

import { MY_ACCOUNT_APP_API_URI, POS_MANAGER_APP_REST_API_URI } from './const';

// UrlObject accepted by next `<Link />` component.
export type UrlOpts = Omit<UrlObject, 'query'> & {
  query?: Record<string, string | number>;
};
type ParametrizedUrlOpts<PathKey extends string | string[]> = UrlOpts &
  (PathKey extends string
    ? {
        [key in PathKey]: string | number;
      }
    : {
        [key in keyof PathKey]: string | number;
      });

// Pre-populated url string - what u see in the browser.
type GeneratedStringUrl = string;

type PopulatedUrl = {
  asPath: (opts?: UrlOpts) => GeneratedStringUrl;
  url: (opts?: UrlOpts) => UrlOpts;
};
type PopulatedParametrizedUrl<PathKey extends string | string[]> = {
  asPath: (opts: ParametrizedUrlOpts<PathKey>) => GeneratedStringUrl;
  url: (opts: ParametrizedUrlOpts<PathKey>) => UrlOpts;
};

function urlWithParam(path: string): PopulatedUrl;
function urlWithParam<PathKey extends string | string[]>(
  path: string,
): PopulatedParametrizedUrl<PathKey>;
function urlWithParam<PathKey extends string>(path: string) {
  if (path !== '/' && !path.startsWith('/')) {
    captureMessage('Path should have leading slash', { extra: { path } });
  }

  const urlParams = [
    'auth',
    'hash',
    'host',
    'hostname',
    'href',
    'pathname',
    'protocol',
    'search',
    'slashes',
    'port',
    'query',
  ];
  const urlProps = (opts?: UrlOpts | ParametrizedUrlOpts<PathKey>): UrlOpts => {
    const urlOpts = pick<UrlOpts>(opts, urlParams);
    const query = omit(opts, urlParams) as UrlOpts['query'];
    return {
      ...urlOpts,
      pathname: `${path}` as const,
      query: { ...query, ...opts?.query },
    };
  };

  return {
    // Pre-populated url string - what u see in the browser.
    asPath: (opts?: UrlOpts | ParametrizedUrlOpts<PathKey>) => {
      const { hash, pathname, query } = urlProps(opts);
      const builtRoute = route({
        pathname: addTrailingSlash(pathname!),
        query: query as ParsedUrlQuery,
        hash,
      });

      const url = new URL(builtRoute, 'http://dummy');

      url.pathname = addTrailingSlash(url.pathname);

      return url.pathname + url.search + url.hash;
    },
    // UrlObject accepted by next `<Link />` component.
    url: (opts?: UrlOpts | ParametrizedUrlOpts<PathKey>) => urlProps(opts),
  };
}

export const url = urlWithParam;

export const paths = {
  ...url('/'),
  home: url('/'),
  about: url('/about/'),
  contact: url('/contact/'),
  archives: {
    ...url('/about/archives/'),
    confirm: url('/about/archives/confirm/'),
  },
  ewarranty: url('/ew/card/'),
  tracking: url('/tracking/'),
  wishlist: url('/wishlist/[skus]/'),
  service: {
    ...url('/service/'),
    myWatchNeedsAService: url('/service/my-watch-needs-a-service/'),
    termsAndConditions: url('/service/general-conditions/'),
    request: url('/service/request/'),
    resistance: url('/service/water-resistance/'),
    result: url<'id'>('/service/received/[id]/'),
    details: url<'params'>('/service/details/[params]/'),
    quote: url<'repair' | 'quote'>('/service/quote/[repair]/[quote]/'),
    payment: {
      ...url<'repair' | 'quote'>('/service/payment/[repair]/[quote]/'),
      confirmation: url('/service/payment/confirmation/'),
    },
    faq: url('/service/faq/'),
    contact: url('/service/contact/'),
  },
  myAccount: {
    ...url('/my-account/'),
    login: url('/my-account/login/'),
    signIn: {
      ...url('/my-account/sign-in/'),
      accountActivation: url('/my-account/sign-in/account-activation/'),
      accountConfirmed: url('/my-account/sign-in/account-confirmed/'),
    },
    myWatches: {
      ...url('/my-account/my-watches/'),
      detail: url<'sku' | 'sn'>('/my-account/my-watches/[sn]/[sku]/'),
    },
    forgottenPassword: {
      ...url('/my-account/forgotten-password/'),
      renew: url('/my-account/forgotten-password/renew/'),
    },
    myWishlist: url('/my-account/my-wishlist/'),
    activate: url('/my-account/activate/'),
    myOrders: {
      ...url('/my-account/order/'),
      orderDetails: url<'orderId'>('/my-account/order/details/[orderId]/'),
      ongoingOrder: url('/my-account/order/ongoing/'),
      orderHistory: url('/my-account/order/history/'),
      returnHistory: url('/my-account/order/returns/'),
      returnOrderDetails: url<'orderId' | 'returnOrderId'>(
        '/my-account/order/details/[orderId]/[returnOrderId]/',
      ),
      serviceHistory: url('/my-account/order/service-history/'),
      tradeInHistory: url('/my-account/order/trade-in-history/'),
    },
    breitlingTrade: url('/my-account/breitling-trade/'),
    breitlingSelect: url('/my-account/breitlingselect/'),
    newAddress: url('/my-account/new-address/'),
    myAddresses: {
      history: url('/my-account/my-addresses/'),
      detail: url<'id'>('/my-account/my-addresses/[id]/'),
      editAddress: url<'id'>('/my-account/my-addresses/[id]/'),
    },
    myDetails: {
      ...url('/my-account/my-details/'),
      myPreferences: url('/my-account/my-details/my-preferences/'),
      myPaymentDetails: url('/my-account/my-details/my-payment-details/'),
      myBoutiques: url('/my-account/my-details/my-boutiques/'),
      myAccount: {
        ...url('/my-account/my-details/my-account/'),
        editDetails: url('/my-account/my-details/my-account/edit-details/'),
        editEmail: {
          ...url('/my-account/my-details/my-account/edit-email/'),
          confirmEmail: url(
            '/my-account/my-details/my-account/edit-email/confirm-email/',
          ),
          confirm: url('/my-account/my-details/my-account/edit-email/confirm/'),
        },
        editPhone: url('/my-account/my-details/my-account/edit-phone/'),
        editPassword: url('/my-account/my-details/my-account/edit-password/'),
      },
    },
  },
  subscribe: {
    confirmation: url('/subscribe/confirmation/'),
  },
  privacyPolicy: url('/privacy-policy/'),
  gdpr: url('/gdpr/'),
  termsOfUse: url('/terms-of-use/'),
  termsAndCondition: url('/terms-and-conditions/'),
  watchComparison: url('/watch-comparison/'),
  store: {
    shoppingBag: url('/store/shopping-bag/'),
    forgottenPassword: url('/store/forgotten-password/'),
    checkout: {
      delivery: url('/store/checkout/delivery/'),
      payment: url('/store/checkout/payment/'),
      review: url('/store/checkout/review/'),
      reviewExpress: url('/store/checkout/review-express/'),
      v12Finance: {
        payment: url('/store/checkout/v12-finance/payment/'),
      },
    },
    order: {
      review: url<'token'>('/store/order/review/[token]/'),
      payment: url<'token'>('/store/order/payment/[token]/'),
      confirmation: url<'id'>('/store/order/confirmation/[id]/'),
    },
    'global-e': {
      checkout: url('/store/global-e/checkout/'),
    },
  },
  stores: {
    breitlingBoutiques: url<'country' | 'slug'>(
      '/stores/breitling-boutiques/[country]/[slug]',
    ),
    appointment: url<'country' | 'slug'>(
      '/stores/appointment/[country]/[slug]',
    ),
  },
  collections: {
    ...url<'slug'>('/collections/[slug]/'),
  },
  allCollections: url('/collections/'),
  watches: {
    ...url('/watches/'),
    all: url('/watches/all/'),
    nfl: url('/watches/nfl-collection/'),
    collection: {
      ...url<'slug'>('/collections/[slug]/'),
      model: {
        product: url<'sku' | 'collection' | 'model'>(
          '/watches/[collection]/[model]/[sku]/',
        ),
      },
    },
  },
  straps: {
    ...url('/straps/'),
    all: {
      ...url('/straps/all/'),
    },
    packs: {
      // ...url<'slug'>('/straps/packs/'),
      ...url<'slug'>('/straps/packs/[slug]/'),
    },
    material: {
      ...url<'material'>('/straps/[material]/'),
      product: url<'sku' | 'material'>('/straps/[material]/[sku]/'),
    },
  },
  eyewear: {
    ...url('/eyewear/'),
    all: {
      ...url('/eyewear/all/'),
    },
    type: {
      ...url<'type'>('/eyewear/[type]/'),
      model: {
        ...url<'type' | 'model'>('/eyewear/[type]/[model]/'),
        product: url<'sku' | 'type' | 'model'>(
          '/eyewear/[type]/[model]/[sku]/',
        ),
      },
    },
  },
  sunglasses: {
    ...url('/sunglasses/'),
    all: {
      ...url('/sunglasses/all/'),
    },
    product: url<'modelSku'>('/sunglasses/[modelSku]/'),
  },
  notFound: url('/404/'),
  payment: {
    ...url<'token'>('/payment/[token]/'),
    success: url<'id'>(`/payment/success/[id]`),
  },
  search: {
    watches: url('/search/watches/'),
    stores: url('/search/stores/'),
    all: url('/search/all/'),
    straps: url('/search/straps/'),
    news: url('/search/news/'),
    contents: url('/search/contents/'),
    eyewear: url('/search/eyewear/'),
  },
  newsArticle: url<'slug'>('/news/[slug]/'),
  news: url('/news/'),
  api: {
    archivesSendEmail: url(`/api/service/archive-send-email/`),
    ecs: {
      config: url('/configurations/ecs.config.json'),
      repairReason: url(`/api/service/repair-reason/`),
      createRepair: url(`/api/service/create-repair/`),
      recommendPrice: url(`/api/service/recommend-price/`),
      repair: {
        ...url(`/api/service/repair/`),
        guest: url(`/api/service/repair/guest/`),
      },
      refuseQuote: url(`/api/service/refuse-quote/`),
      updateRepair: url(`/api/service/update-repair/`),
      tracking: url(`/api/service/tracking/`),
      watchDetails: url(`/api/service/watch-details/`),
    },
    fast: {
      getPaymentMethods: url('/api/service/payment-methods/'),
      paymentSession: url('/api/service/payment-session/'),
    },
    paymentMethods: url('/api/payment-methods/'),
    order: {
      tokenCreate: url<'id'>('/api/order/token-create/[id]/'),
      status: url('/api/order/status/'),
      getByCheckout: url('/api/order/get-by-checkout/'),
    },
    myaccount: url('/api/my-account/my-account/'),
    tokenVerification: url('/api/captcha/token-validation/'),
    appointment: `${POS_MANAGER_APP_REST_API_URI}appointment/`,
    wishlist: `${MY_ACCOUNT_APP_API_URI}wishlist/me`,
    myWatches: `${MY_ACCOUNT_APP_API_URI}my-watches/me/`,
    myWarranty: `${MY_ACCOUNT_APP_API_URI}my-watches/warranty/`,
    myAny: `${MY_ACCOUNT_APP_API_URI}my-watches/any/`,
    newsLetter: {
      user: `${MY_ACCOUNT_APP_API_URI}newsletter/user/`,
      guestRequest: `${MY_ACCOUNT_APP_API_URI}newsletter/guest/request/`,
      guestSubmit: `${MY_ACCOUNT_APP_API_URI}newsletter/guest/submit/`,
      unsubscribe: `${MY_ACCOUNT_APP_API_URI}newsletter/user/`,
      formSubscription: `${MY_ACCOUNT_APP_API_URI}form-subscription/`,
      subscribeConfirmation: `${MY_ACCOUNT_APP_API_URI}form-subscription/confirmation/`,
    },
    catalogDownload: {
      user: `${MY_ACCOUNT_APP_API_URI}chronolog/user/request/`,
      guestRequest: `${MY_ACCOUNT_APP_API_URI}chronolog/guest/request/`,
      verify: `${MY_ACCOUNT_APP_API_URI}chronolog/verify/`,
    },
    nft: `${MY_ACCOUNT_APP_API_URI}nft/`,
    extendable: `${MY_ACCOUNT_APP_API_URI}my-watches/extendable/`,
    notifyMe: {
      user: `${MY_ACCOUNT_APP_API_URI}notifyme/`,
      guestSubmit: `${MY_ACCOUNT_APP_API_URI}notifyme/guest/request/`,
      guestConfirm: `${MY_ACCOUNT_APP_API_URI}notifyme/guest/submit/`,
    },
    user: {
      check: url(`/api/user/check/`),
      create: url('/api/user/create/'),
      update: url('/api/user/update/'),
    },
    watch: {
      watchInfo: `${MY_ACCOUNT_APP_API_URI}my-watches/warranty/`,
      watchRegister: url(`/api/service/register-watch/`),
    },
    product: {
      strapSizes: url('/api/product/strap-sizes/'),
    },
    products: url('/api/products/'),
    stock: {
      productStock: `${POS_MANAGER_APP_REST_API_URI}/stock/`,
    },
    getWatchesWithoutChannel: url(
      `/api/service/watch-details-without-channel/`,
    ),
    watchAvailGuest: `${MY_ACCOUNT_APP_API_URI}watch-avail/guest`,
    watchAvail: `${MY_ACCOUNT_APP_API_URI}watch-avail`,
    productDetail: url('/api/product-detail/'),
    abandonCart: `${MY_ACCOUNT_APP_API_URI}abandoned-cart/logout`,
    express: {
      resetCheckoutAddress: url(`/api/express/reset-checkout-address`),
      initCheckoutAddress: url(`/api/express/init-checkout-address`),
      gwpProductUpdate: url(`/api/express/gwp-product-update`),
      updateOrder: url(`/api/express/update-order`),
      updateCheckoutBillingAddress: url(
        `/api/express/update-checkout-billing-address`,
      ),
    },
  },
};

export type Paths = typeof paths;
